// CSV export, for every screen that has one.
//
// Promoted from `Operations.shared.js` (which had the tests) after a second,
// subtly different copy appeared in the cash desk's screen — different line ending,
// different digit handling — so the same «خروجی» button produced two dialects of
// file depending on which screen it sat on. Both screens now import from here.
//
// The rules below are all about ONE consumer: Excel, in Persian, on Windows. Every
// one of them was a real defect first.

import { DEFAULT_CURRENCY, currencyInfo, toDisplay } from './currency';

/**
 * Rows → a CSV string Excel opens correctly.
 *
 *   • **UTF-8 BOM.** Without it Excel reads the file in the system codepage and
 *     every Persian column is mojibake. It is the single most important byte here.
 *   • **Every cell quoted.** A comma or a newline inside a description — «تعمیر
 *     یونیت، اتاق ۲» — shifts every later column on that row, silently.
 *   • **CRLF.** Excel accepts LF, but Notepad and a handful of older importers put
 *     the whole file on one line. The file is for sending to an accountant, not for
 *     reading in a terminal.
 */
export function toCsv(headers, rows) {
  const esc = (cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`;
  return '﻿' + [headers, ...rows].map((r) => r.map(esc).join(',')).join('\r\n');
}

/**
 * Money for a spreadsheet: TOMAN, plain ASCII digits, no grouping.
 *
 * Persian digits are the right answer on screen and the wrong one in a file: Excel
 * reads «۵۰۰٬۰۰۰» as TEXT, so the column cannot be summed, sorted or charted — and
 * the person who exported it discovers that only after building a pivot on it.
 * Grouping separators lose a cell to the same fate in some locales.
 *
 * `currency` is the document's or report's (blank is IRR): IRR is whole toman,
 * exactly as always; another currency is its display unit with its own fixed
 * decimals and a '.' point — «1234.50» USD, «12.345» KWD — never mixed with IRR in
 * one column, because a file never holds two clinics' money.
 */
export const csvMoney = (rial, currency) => {
  const info = currencyInfo(currency);
  if (info.code === DEFAULT_CURRENCY) return String(Math.round((Number(rial) || 0) / 10));
  return toDisplay(Number(rial) || 0, info.code).toFixed(info.maxDisplayDecimals);
};

/** A count for a spreadsheet — same reasoning, no conversion. */
export const csvNumber = (n) => String(Number(n) || 0);

/** A percentage, one decimal, ASCII. Empty for a value that does not exist. */
export const csvPercent = (v) =>
  (v === null || v === undefined || Number.isNaN(v) ? '' : (Math.round(Number(v) * 10) / 10).toString());

/**
 * Trigger the download.
 *
 * `revokeObjectURL` is not tidiness: without it the Blob is pinned for the life of
 * the document, and an operator exporting a large ledger a few times a day leaks it
 * every time.
 */
export function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * A header block above the table: what this file is, and what it excludes.
 *
 * An exported table is separated from its filters the moment it is attached to an
 * email, and «درآمد ۴۸ میلیون» means nothing without «۱ تا ۲۰ شهریور، دکتر الف».
 * Excel ignores the leading lines; the person reading it does not.
 */
export function csvHeaderBlock({ title, range, filters = [] }) {
  const lines = [[title]];
  if (range) lines.push([range]);
  for (const f of filters) if (f) lines.push([f]);
  lines.push([]);
  return lines;
}
